/**
 * EnvironmentManager — State + logic for environment switching.
 *
 * Responsibilities:
 * - Resolves EnvironmentId to descriptors (spotlight / studio / hdri / road)
 * - Tracks active environment state (id, background mode, ground, fog)
 * - Maintains a sticky override store per environment ID
 * - `onChange` callback drives the R3F EnvironmentComponent to re-render
 * - Does NOT perform any Three.js calls — that is the component's job
 *
 * Decoupled from IBL, sunlight, and shadow — those are now standalone
 * subsystems. Use PresetManager to orchestrate all systems at once.
 *
 * @see docs/ims-engine-api/10-feature-environment.md
 */

import type {
  EnvironmentConfig,
  EnvironmentOverrides,
  BackgroundDescriptor,
  EnvironmentDescriptor,
  FogDescriptor,
  GroundVariant,
} from '../api/types';
import { resolveDescriptor, SPOTLIGHT_THEMES } from './EnvironmentDescriptors';

// ---------------------------------------------------------------------------
// EnvironmentState — consumed by the R3F EnvironmentComponent
// ---------------------------------------------------------------------------

/** Full state snapshot consumed by the R3F EnvironmentComponent. */
export interface EnvironmentState {
  /** Active environment identifier (descriptor ID or legacy preset ID). */
  id: string;
  /** Background rendering mode (color, hdr-blur, dome, none). */
  background: BackgroundDescriptor;
  /** Animate black-fade transition on environment switch. */
  transition: boolean;
  /** When false, all environment features are disabled. */
  enabled: boolean;
  /** Active descriptor (null for legacy HDRI-only mode). */
  descriptor: EnvironmentDescriptor | null;
  /** Merged overrides for the active environment. */
  overrides: Partial<EnvironmentOverrides>;
  /**
   * Effective fog config: descriptor fog merged with stored fog overrides.
   * null when the environment has no fog (e.g. studio / hdri).
   */
  fogConfig: FogDescriptor | null;
  /**
   * Effective ground color: stored groundColor override or descriptor ground.color.
   * undefined when the environment has no ground.
   */
  groundColor: string | undefined;
  /** Active ground variant ('concrete', 'reflective', or 'road'). */
  groundVariant: GroundVariant;
  /** Planar reflection intensity (0–1). Only applies when groundVariant = 'reflective'. */
  groundReflectivity: number;
  /** Mirror road markings for RHD markets. Only applies when groundVariant = 'road'. */
  roadMirror: boolean;
  /** Road surface texture variant. Only applies when groundVariant = 'road'. */
  roadSurface: 'road' | 'racetrack';
}

// ---------------------------------------------------------------------------
// EnvironmentManager
// ---------------------------------------------------------------------------

/** Default descriptor for initial state (spotlight). */
const DEFAULT_DESCRIPTOR = resolveDescriptor('spotlight')!;

export class EnvironmentManager {
  private _id        = DEFAULT_DESCRIPTOR.id as string;
  private _background: BackgroundDescriptor = DEFAULT_DESCRIPTOR.background;
  private _transition = true;
  private _enabled    = true;
  private _descriptor: EnvironmentDescriptor | null = DEFAULT_DESCRIPTOR;
  private _overrides    = new Map<string, Partial<EnvironmentOverrides>>();
  private _fogConfig:   FogDescriptor | null   = DEFAULT_DESCRIPTOR.fog ?? null;
  private _groundColor: string | undefined     = DEFAULT_DESCRIPTOR.ground?.color;
  private _groundVariant: GroundVariant = DEFAULT_DESCRIPTOR.ground?.variant ?? 'concrete';
  private _groundReflectivity: number          = 0;
  private _roadMirror: boolean                 = false;
  private _roadSurface: 'road' | 'racetrack'   = 'road';

  onChange?: (state: EnvironmentState) => void;

  // ---------------------------------------------------------------------------
  // apply — main entry point
  // ---------------------------------------------------------------------------

  /**
   * Switch environment or apply toggle flags.
   *
   * @param config  EnvironmentId string or EnvironmentConfig ({ enabled, transition })
   * @param overrides  Optional overrides (only when config is a string ID)
   */
  apply(
    config: string | EnvironmentConfig,
    overrides?: EnvironmentOverrides,
  ): void {
    if (typeof config === 'string') {
      this._switchTo(config, overrides);
    } else {
      this._applyLegacy(config);
    }
    this._notify();
  }

  // ---------------------------------------------------------------------------
  // Private — switch to environment by ID
  // ---------------------------------------------------------------------------

  private _switchTo(id: string, overrides?: EnvironmentOverrides): void {
    // Store overrides if provided
    if (overrides) {
      if (overrides._reset) {
        this._overrides.delete(id);
      } else {
        const existing = this._overrides.get(id) ?? {};
        const merged: Partial<EnvironmentOverrides> = { ...existing, ...overrides };
        this._overrides.set(id, merged);
      }
    }

    // Resolve descriptor (spotlight / studio / hdri / road)
    const descriptor = resolveDescriptor(id);
    if (descriptor) {
      this._descriptor = descriptor;
      this._id         = descriptor.id;
      const stored = this._overrides.get(this._id) ?? {};

      // Resolve named spotlight theme (base values, lowest priority among overrides)
      const theme = (stored.spotlightTheme && descriptor.id === 'spotlight')
        ? (SPOTLIGHT_THEMES[stored.spotlightTheme] ?? null)
        : null;

      // Background: explicit override > theme > descriptor
      const bg = descriptor.background;
      const bgColor = stored.backgroundColor ?? theme?.backgroundColor;
      this._background = (bgColor && bg.type === 'color')
        ? { ...bg, value: bgColor }
        : bg;

      // Fog: explicit override > theme > descriptor
      const fogColor   = stored.fog?.color   ?? theme?.fogColor;
      const fogDensity = stored.fog?.density  ?? theme?.fogDensity;
      if (descriptor.fog) {
        this._fogConfig = {
          ...descriptor.fog,
          ...(fogColor   !== undefined ? { color:   fogColor   } : {}),
          ...(fogDensity !== undefined ? { density: fogDensity } : {}),
        };
      } else {
        this._fogConfig = null;
      }

      // Ground color: explicit override > theme > descriptor
      this._groundColor = stored.groundColor ?? theme?.groundColor ?? descriptor.ground?.color;

      // Ground variant: explicit override > descriptor default
      this._groundVariant = stored.groundVariant ?? descriptor.ground?.variant ?? 'concrete';

      // Ground reflectivity: only when variant is 'reflective'
      this._groundReflectivity = this._groundVariant === 'reflective'
        ? (stored.groundReflectivity ?? 0.5)
        : 0;

      // Road mirror: only when variant is 'road' (RHD markets)
      this._roadMirror = this._groundVariant === 'road'
        ? (stored.roadMirror ?? false)
        : false;

      // Road surface: only when variant is 'road'
      this._roadSurface = this._groundVariant === 'road'
        ? (stored.roadSurface ?? 'road')
        : 'road';

      return;
    }

    console.warn(`[EnvironmentManager] Unknown environment ID: "${id}"`);
  }

  // ---------------------------------------------------------------------------
  // Private — EnvironmentConfig flag handling
  // ---------------------------------------------------------------------------

  private _applyLegacy(config: EnvironmentConfig): void {
    if (config.transition !== undefined) this._transition = config.transition;
    if (config.enabled !== undefined)    this._enabled    = config.enabled;
  }

  // ---------------------------------------------------------------------------
  // Getters / state
  // ---------------------------------------------------------------------------

  getState(): EnvironmentState {
    return {
      id:          this._id,
      background:  this._background,
      transition:  this._transition,
      enabled:     this._enabled,
      descriptor:  this._descriptor,
      overrides:   this._overrides.get(this._id) ?? {},
      fogConfig:          this._fogConfig,
      groundColor:        this._groundColor,
      groundVariant:      this._groundVariant,
      groundReflectivity: this._groundReflectivity,
      roadMirror:         this._roadMirror,
      roadSurface:        this._roadSurface,
    };
  }

  get id():         string              { return this._id; }
  get background(): BackgroundDescriptor { return this._background; }
  get transition(): boolean             { return this._transition; }
  get enabled():    boolean             { return this._enabled; }
  get descriptor(): EnvironmentDescriptor | null { return this._descriptor; }

  /** Get stored overrides for a specific environment ID. */
  getOverrides(envId: string): Partial<EnvironmentOverrides> {
    return this._overrides.get(envId) ?? {};
  }

  // ---------------------------------------------------------------------------
  // dispose
  // ---------------------------------------------------------------------------

  dispose(): void {
    this.onChange = undefined;
    this._overrides.clear();
  }

  private _notify(): void {
    this.onChange?.(this.getState());
  }
}
